<template>
  <v-container>

    <h3>{{ $t('members.title') }} <span v-if="!loading">({{ filteredAccounts.length }})</span> :</h3>

    <v-row class="mt-1" v-if="display.mdAndUp">
      <v-col cols="12" md="1">
        <v-select
            v-model="filter_club"
            :items="clubShortcuts"
            :label="$t('members.filters.club')"
            variant="outlined"
            hide-details
            density="compact"
            @keyup.enter="fetchAccounts"
        />
      </v-col>
      <v-col cols="12" md="1">
        <v-text-field
            v-model="filter_min_year"
            :label="$t('members.filters.min_year')"
            variant="outlined"
            hide-details
            density="compact"
        />
      </v-col>
      <v-col cols="12" md="1">
        <v-text-field
            v-model="filter_max_year"
            :label="$t('members.filters.max_year')"
            variant="outlined"
            hide-details
            density="compact"
        />
      </v-col>
      <v-col>
        <v-btn color="primary" @click="fetchAccounts" :disabled="!valid">{{ $t('members.filters.apply') }}</v-btn>
      </v-col>
    </v-row>

    <v-expansion-panels v-else>
      <v-expansion-panel>
        <v-expansion-panel-title color="primary">{{ $t('members.filters_title') }}</v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-col cols="12">
            <v-select
                v-model="filter_club"
                :items="clubShortcuts"
                :label="$t('members.filters.club')"
                variant="outlined"
                hide-details
                density="compact"
                @keyup.enter="fetchAccounts"
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
                v-model="filter_min_year"
                :label="$t('members.filters.min_year')"
                variant="outlined"
                hide-details
                density="compact"
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
                v-model="filter_max_year"
                :label="$t('members.filters.max_year')"
                variant="outlined"
                hide-details
                density="compact"
            />
          </v-col>
          <v-col>
            <v-btn color="primary" @click="fetchAccounts" :disabled="!valid">{{ $t('members.filters.apply') }}</v-btn>
          </v-col>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>


    <v-row>
      <v-col>
        <v-data-table
            density="comfortable"
            :headers="accountHeaders"
            :items="filteredAccounts"
            item-key="id"
            :items-per-page="500"
            :sort-by="sortBy"
            :loading="loading"
            loading-text="general.loading"
            striped="even"
            hover
        >
          <template
            v-for="key in TEXT_FILTER_COLUMNS"
            :key="key"
            v-slot:[`header.${key}`]="{ column, getSortIcon }"
          >
            <div class="member-header-cell">
              <div class="v-data-table-header__content">
                <span>{{ column.title }}</span>
                <v-icon
                  v-if="column.sortable"
                  class="v-data-table-header__sort-icon"
                  :icon="getSortIcon(column)"
                />
              </div>
              <v-text-field
                v-model="columnFilters[key]"
                class="member-header-filter"
                density="compact"
                variant="outlined"
                hide-details
                :placeholder="$t('members.filter_placeholder')"
                @click.stop
                @mousedown.stop
              />
            </div>
          </template>

          <template v-slot:[`header.referee.lvl`]="{ column, getSortIcon }">
            <div class="member-header-cell">
              <div class="v-data-table-header__content">
                <span>{{ column.title }}</span>
                <v-icon
                  v-if="column.sortable"
                  class="v-data-table-header__sort-icon"
                  :icon="getSortIcon(column)"
                />
              </div>
              <v-select
                v-model="refereeFilter"
                class="member-header-filter"
                :items="LVL_SELECT_ITEMS"
                density="compact"
                variant="outlined"
                hide-details
                clearable
                multiple
                :placeholder="$t('members.filter_placeholder')"
                @click.stop
                @mousedown.stop
              />
            </div>
          </template>

          <template v-slot:[`header.coach.lvl`]="{ column, getSortIcon }">
            <div class="member-header-cell">
              <div class="v-data-table-header__content">
                <span>{{ column.title }}</span>
                <v-icon
                  v-if="column.sortable"
                  class="v-data-table-header__sort-icon"
                  :icon="getSortIcon(column)"
                />
              </div>
              <v-select
                v-model="coachFilter"
                class="member-header-filter"
                :items="LVL_SELECT_ITEMS"
                density="compact"
                variant="outlined"
                hide-details
                clearable
                multiple
                :placeholder="$t('members.filter_placeholder')"
                @click.stop
                @mousedown.stop
              />
            </div>
          </template>

          <template v-slot:[`header.actions`]="{ column }">
            <div class="member-header-cell">
              <div class="v-data-table-header__content">
                <span>{{ column.title }}</span>
              </div>
            </div>
          </template>

          <template v-slot:[`item.last_name`]="{ item }">
            <v-tooltip location="top center" origin="auto"
                       :text="$t('general.last_activity')+ ': ' + $formatDateTime(item.last_activity)">
              <template v-slot:activator="{ props }">
                <router-link v-bind="props" :to="'/ucet/' + item.id" style="white-space: nowrap;">
                  {{ item.last_name }} {{ item.first_name }}
                </router-link>
              </template>
            </v-tooltip>
          </template>

          <template v-slot:[`item.referee.lvl`]="{ item }">
            <v-tooltip location="top center" origin="auto" v-if="item.referee">
              <template v-slot:activator="{ props }">
                <span v-bind="props">{{ item.referee.lvl }}. {{ $t('members.class') }}</span>
              </template>
              <div>{{ $t('members.validity') }} {{
                  $formatDate(item.referee.valid_from) + " - " + $formatDate(item.referee.valid_to)
                }}
              </div>
              <div>{{ $t('members.note') }} {{ item.referee.description }}</div>
            </v-tooltip>
          </template>

          <template v-slot:[`item.coach.lvl`]="{ item }">
            <v-tooltip location="top center" origin="auto" v-if="item.coach">
              <template v-slot:activator="{ props }">
                <span v-bind="props">{{ item.coach.lvl }}. {{ $t('members.class') }}</span>
              </template>
              <div>{{ $t('members.validity') }} {{
                  $formatDate(item.coach.valid_from) + " - " + $formatDate(item.coach.valid_to)
                }}
              </div>
              <div>{{ $t('members.note') }} {{ item.coach.description }}</div>
            </v-tooltip>
          </template>

          <template v-slot:[`item.last_activity`]="{ item }">
            {{ $formatDateTime(item.last_activity) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }" v-if="manager">
            <v-list-item-action>
              <v-icon @click="editRoles(item)" color="blue" size="33" class="mr-2"
                      :title="$t('members.edit_roles')">mdi-account-hard-hat
              </v-icon>
            </v-list-item-action>
          </template>


          <template v-slot:no-data>
            <v-alert type="info" class="ma-4">
              {{ $t('general.no_data') }}
            </v-alert>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>

  <RoleDialog
      v-model="roleDialogOpen"
      :account-id="selectedAccountId"
      @close-dialog="closeAndReloadRoles"/>

  <ScrollTopButton/>

</template>


<script setup>
import {ref, onMounted, computed, reactive} from 'vue';
import apiClient from '/api.js';
import {useAuthStore} from '/auth.js';
import RoleDialog from "@/components/shared/RoleDialog.vue";
import ScrollTopButton from '@/components/shared/ScrollTopButton.vue';
import {useDisplay} from 'vuetify';
import {useI18n} from 'vue-i18n';

const {t} = useI18n();
const display = ref(useDisplay());
const authStore = useAuthStore();
const valid = ref(true);
const accounts = ref([]);
const loading = ref(true);
const accountsCount = ref(0);

const filter_club = ref("")
const filter_min_year = ref("")
const filter_max_year = ref("")

const manager = authStore.isManager();

const TEXT_FILTER_COLUMNS = ['last_name', 'index']
const LVL_SELECT_ITEMS = computed(() => [
  { title: `1. ${t('members.class')}`, value: '1' },
  { title: `2. ${t('members.class')}`, value: '2' },
  { title: `3. ${t('members.class')}`, value: '3' },
])

const columnFilters = reactive({
  last_name: '',
  index: '',
})
const refereeFilter = ref([])
const coachFilter = ref([])

const accountHeaders = computed(() => [
  { title: t('members.name'), key: 'last_name', value: 'last_name', sortable: true, align: 'start' },
  { title: t('members.index'), key: 'index', value: 'index', sortable: true, align: 'start' },
  { title: t('members.referee'), key: 'referee.lvl', value: 'referee.lvl', sortable: true, align: 'start' },
  { title: t('members.coach'), key: 'coach.lvl', value: 'coach.lvl', sortable: true, align: 'start' },
  ...(manager ? [{ title: t('members.actions'), key: 'actions', value: 'actions', sortable: false, width: '120', align: 'start' }] : []),
]);

const sortBy = [{key: 'last_name', order: 'asc'}];

const filteredAccounts = computed(() => {
  const minYear = parseInt(filter_min_year.value) || null;
  const maxYear = parseInt(filter_max_year.value) || null;
  const selectedReferee = refereeFilter.value || []
  const selectedCoach = coachFilter.value || []

  return accounts.value.filter(account => {
    if (minYear && account.birth_year < minYear) return false;
    if (maxYear && account.birth_year > maxYear) return false;

    const nameQuery = String(columnFilters.last_name || '').trim().toLowerCase()
    if (nameQuery) {
      const haystack = `${account.last_name || ''} ${account.first_name || ''}`.toLowerCase()
      if (!haystack.includes(nameQuery)) return false
    }

    const indexQuery = String(columnFilters.index || '').trim().toLowerCase()
    if (indexQuery) {
      if (!String(account.index || '').toLowerCase().includes(indexQuery)) return false
    }

    if (selectedReferee.length) {
      if (!account.referee || !selectedReferee.includes(String(account.referee.lvl))) return false
    }

    if (selectedCoach.length) {
      if (!account.coach || !selectedCoach.includes(String(account.coach.lvl))) return false
    }

    return true;
  });
});

const clubShortcuts = ref([]);

const fetchAccounts = async () => {
  try {
    const params = {};

    if (filter_club.value.trim()) {
      params.club_shortcut = filter_club.value.trim();
    }

    const response = await apiClient.get('/members_all/', {params});

    accounts.value = response.data.all_members
    clubShortcuts.value = ["", ...response.data.club_shortcuts];
    accountsCount.value = accounts.value.length;
  } catch (error) {
    console.error('Error fetching accounts:', error);
  } finally {
    loading.value = false;
  }
};


onMounted(() => {
  fetchAccounts();
});


const roleDialogOpen = ref(false);
const selectedAccountId = ref(-1);

const editRoles = (item) => {
  selectedAccountId.value = item.id;
  roleDialogOpen.value = true;
};

const closeAndReloadRoles = () => {
  roleDialogOpen.value = false
  fetchAccounts()
}

</script>


<style scoped>

:deep() .v-table .v-table__wrapper > table > thead > tr > th:not(:last-child) {
  border-right: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}

:deep() .v-table .v-table__wrapper > table > tbody > tr > td:not(:last-child), .v-table .v-table__wrapper > table > tbody > tr > th:not(:last-child) {
  border-right: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.member-header-cell {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  width: 100%;
  min-width: 0;
  padding: 8px 0;
  box-sizing: border-box;
}

.member-header-filter {
  width: 100% !important;
  max-width: 100%;
  font-weight: 400;
  text-transform: none;
}

.member-header-filter :deep(.v-input),
.member-header-filter :deep(.v-input__control),
.member-header-filter :deep(.v-field) {
  width: 100%;
}

.member-header-filter :deep(.v-field) {
  font-size: 0.8125rem;
}

.member-header-filter :deep(.v-field__input) {
  min-height: 32px;
  padding-top: 4px;
  padding-bottom: 4px;
}

:deep(th.v-data-table__th) {
  vertical-align: top;
}

:deep(th.v-data-table__th .member-header-cell) {
  align-self: stretch;
  flex: 1 1 auto;
  width: 100%;
}
</style>
